// =============================================================================
// TOLOnews Store Service
// Based on TOLOnews_Data_Architecture_Specification.md
// =============================================================================

import { supabase } from './supabaseClient';
import {
  Story,
  PlanningItem,
  PlanningItemWithUser,
  ContentItem,
  ContentVersion,
  Translation,
  Collection,
  CollectionItem,
  Component,
  CoordinatedRelease,
  ReleaseTarget,
  Approval,
  EditorialComment,
  WorkflowHistory,
  UpdateNotification,
  Terminology,
  User,
  StoryWithContent,
  ContentItemWithVersion,
  CollectionWithItems,
  NinjsPayload,
  LanguageCode,
  Platform,
  ItemType,
  Desk,
  WorkflowStatus,
  CollectionType,
  OrderingMode,
  TranslationMethod,
  ApprovalStage,
  ApprovalDecision,
  BroadcastItemType,
  generateStoryGuid,
  generateContentGuid,
} from '../types';


// =============================================================================
// STORIES
// =============================================================================

export async function createStory(data: {
  slug: string;
  canonical_language?: LanguageCode;
  urgency?: number;
  is_breaking?: boolean;
  breaking_level?: 'flash' | 'urgent' | 'developing';
  subjects?: object[];
}): Promise<Story | null> {
  const user = await getCurrentUser();
  if (!user) return null;

  const guid = generateStoryGuid(data.slug);

  const { data: story, error } = await supabase
    .from('stories')
    .insert({
      guid,
      slug: data.slug,
      canonical_language: data.canonical_language || 'fa-AF',
      urgency: data.urgency || 5,
      is_breaking: data.is_breaking || false,
      breaking_level: data.breaking_level,
      subjects: data.subjects || [],
      created_by: user.id,
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating story:', error);
    return null;
  }

  await logAction('create_story', 'story', story.id);
  return story;
}

export async function getStory(id: string): Promise<Story | null> {
  const { data, error } = await supabase
    .from('stories')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    console.error('Error fetching story:', error);
    return null;
  }

  return data;
}

export async function getStoryWithContent(id: string): Promise<StoryWithContent | null> {
  console.log('getStoryWithContent called for id:', id);

  const { data: story, error: storyError } = await supabase
    .from('stories')
    .select('*')
    .eq('id', id)
    .single();

  if (storyError || !story) {
    console.error('Error fetching story:', storyError);
    return null;
  }

  // Fetch planning items with assigned user
  const { data: planningItems } = await supabase
    .from('planning_items')
    .select(`
      *,
      assigned_user:profiles!planning_items_assigned_to_fkey(*)
    `)
    .eq('story_id', id)
    .order('priority', { ascending: false });

  // Fetch content items with current versions and assigned user
  const { data: contentItems, error: contentError } = await supabase
    .from('content_items')
    .select(`
      *,
      current_version:content_versions!fk_current_version(*),
      published_version:content_versions!fk_published_version(*),
      assigned_user:profiles!content_items_assigned_to_fkey(*)
    `)
    .eq('story_id', id)
    .order('is_canonical', { ascending: false });

  if (contentError) {
    console.error('Error fetching content items:', contentError);
  }

  console.log('Content items fetched:', contentItems?.length || 0);
  if (contentItems && contentItems.length > 0) {
    console.log('First content item:', {
      id: contentItems[0].id,
      current_version_id: contentItems[0].current_version_id,
      has_current_version: !!contentItems[0].current_version,
      current_version_headline: contentItems[0].current_version?.headline,
    });
  }

  // Fetch components
  const { data: components } = await supabase
    .from('components')
    .select('*')
    .eq('story_id', id)
    .eq('is_active', true);

  return {
    ...story,
    planning_items: planningItems || [],
    content_items: contentItems || [],
    components: components || [],
  };
}

export async function getStories(options?: {
  limit?: number;
  offset?: number;
  breaking_only?: boolean;
  archived?: boolean;
  search?: string;
}): Promise<Story[]> {
  let query = supabase
    .from('stories')
    .select('*')
    .order('created_at', { ascending: false });

  if (options?.breaking_only) {
    query = query.eq('is_breaking', true);
  }

  if (!options?.archived) {
    query = query.is('archived_at', null);
  }

  if (options?.search) {
    query = query.ilike('slug', `%${options.search}%`);
  }

  if (options?.limit) {
    query = query.limit(options.limit);
  }

  if (options?.offset) {
    query = query.range(options.offset, options.offset + (options.limit || 50) - 1);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching stories:', error);
    return [];
  }

  return data || [];
}

export async function updateStory(id: string, updates: Partial<Story>): Promise<Story | null> {
  const { data, error } = await supabase
    .from('stories')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating story:', error);
    return null;
  }

  await logAction('update_story', 'story', id);
  return data;
}

export async function archiveStory(id: string): Promise<boolean> {
  const { error } = await supabase
    .from('stories')
    .update({ archived_at: new Date().toISOString() })
    .eq('id', id);

  if (error) {
    console.error('Error archiving story:', error);
    return false;
  }

  await logAction('archive_story', 'story', id);
  return true;
}


// =============================================================================
// PLANNING ITEMS (Assignments)
// Per workflow: Planning Item = intent/assignment, Content Item = actual work
// =============================================================================

export async function createPlanningItem(data: {
  story_id: string;
  title?: string;
  platform: Platform;
  language: LanguageCode;
  item_type: ItemType;
  assigned_desk: Desk;
  assigned_to?: string;
  due_date?: string;
  priority?: number;
  notes?: string;
}): Promise<PlanningItem | null> {
  const user = await getCurrentUser();
  if (!user) return null;

  // Auto-generate title if not provided
  const title = data.title || `${data.platform.toUpperCase()} ${data.item_type} (${data.language})`;

  const { data: item, error } = await supabase
    .from('planning_items')
    .insert({
      ...data,
      title,
      priority: data.priority || 50,
      status: 'planned',
      created_by: user.id,
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating planning item:', error);
    return null;
  }

  await logAction('create_planning_item', 'planning_item', item.id);
  return item;
}

export async function getPlanningItems(storyId: string): Promise<PlanningItemWithUser[]> {
  const { data, error } = await supabase
    .from('planning_items')
    .select(`
      *,
      assigned_user:profiles!planning_items_assigned_to_fkey(*)
    `)
    .eq('story_id', storyId)
    .order('priority', { ascending: false });

  if (error) {
    console.error('Error fetching planning items:', error);
    return [];
  }

  return data || [];
}

export async function getPlanningItemsForUser(userId: string): Promise<PlanningItemWithUser[]> {
  const { data, error } = await supabase
    .from('planning_items')
    .select(`
      *,
      assigned_user:profiles!planning_items_assigned_to_fkey(*),
      story:stories(*)
    `)
    .eq('assigned_to', userId)
    .in('status', ['planned', 'confirming', 'assigned', 'in_progress'])
    .order('due_date', { ascending: true });

  if (error) {
    console.error('Error fetching user planning items:', error);
    return [];
  }

  return data || [];
}

export async function updatePlanningItem(id: string, updates: Partial<PlanningItem>): Promise<PlanningItem | null> {
  const { data, error } = await supabase
    .from('planning_items')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating planning item:', error);
    return null;
  }

  await logAction('update_planning_item', 'planning_item', id);
  return data;
}

// "Start Working" - creates Content Item when reporter begins work
export async function startWorkingOnAssignment(planningId: string): Promise<ContentItem | null> {
  // Get the planning item with story info
  const { data: planning, error: planningError } = await supabase
    .from('planning_items')
    .select('*, story:stories(*)')
    .eq('id', planningId)
    .single();

  if (planningError || !planning) {
    console.error('Error fetching planning item:', planningError);
    return null;
  }

  // Check if already started (content item already exists)
  if (planning.promoted_to_content_at) {
    console.error('Assignment already started');
    return null;
  }

  // Check if this is the first content item for this story
  const { count } = await supabase
    .from('content_items')
    .select('*', { count: 'exact', head: true })
    .eq('story_id', planning.story_id);

  const isFirstContentItem = (count || 0) === 0;

  // Create content item from planning item
  const contentItem = await createContentItem({
    story_id: planning.story_id,
    planning_id: planningId,
    language: planning.language,
    item_type: planning.item_type,
    platform: planning.platform,
    is_canonical: isFirstContentItem,
  });

  if (contentItem) {
    // Update planning item status
    await supabase
      .from('planning_items')
      .update({
        status: 'in_progress',
        promoted_to_content_at: new Date().toISOString(),
      })
      .eq('id', planningId);

    await logAction('start_working', 'planning_item', planningId, { content_item_id: contentItem.id });
  }

  return contentItem;
}

// Get planning item with its associated content item (if any)
export async function getPlanningItemWithContent(planningId: string): Promise<PlanningItemWithUser | null> {
  const { data: planning, error: planningError } = await supabase
    .from('planning_items')
    .select(`
      *,
      assigned_user:profiles!planning_items_assigned_to_fkey(*)
    `)
    .eq('id', planningId)
    .single();

  if (planningError || !planning) {
    console.error('Error fetching planning item:', planningError);
    return null;
  }

  // If promoted, find the content item
  if (planning.promoted_to_content_at) {
    const { data: contentItem } = await supabase
      .from('content_items')
      .select('*')
      .eq('planning_id', planningId)
      .single();

    return { ...planning, content_item: contentItem };
  }

  return planning;
}


// =============================================================================
// CONTENT ITEMS
// =============================================================================

export async function createContentItem(data: {
  story_id: string;
  planning_id?: string;
  language: LanguageCode;
  item_type: ItemType;
  platform: Platform;
  is_canonical?: boolean;
  derived_from_content_id?: string;
  assigned_to?: string;
}): Promise<ContentItem | null> {
  const user = await getCurrentUser();
  if (!user) {
    console.error('createContentItem: No authenticated user');
    return null;
  }

  // Get story for GUID generation
  const story = await getStory(data.story_id);
  if (!story) {
    console.error('createContentItem: Story not found:', data.story_id);
    return null;
  }

  const guid = generateContentGuid(story.slug, data.language, data.platform);
  console.log('createContentItem: Creating with GUID:', guid);

  const insertData = {
    guid,
    story_id: data.story_id,
    planning_id: data.planning_id,
    language: data.language,
    item_type: data.item_type,
    platform: data.platform,
    is_canonical: data.is_canonical || false,
    derived_from_content_id: data.derived_from_content_id,
    assigned_to: data.assigned_to,
    workflow_status: 'draft' as const,
    post_publication_review_required: false,
    created_by: user.id,
  };
  console.log('createContentItem: Insert data:', insertData);

  const { data: item, error } = await supabase
    .from('content_items')
    .insert(insertData)
    .select()
    .single();

  if (error) {
    console.error('Error creating content item:', error);
    console.error('Error details:', JSON.stringify(error, null, 2));
    return null;
  }

  console.log('createContentItem: Created successfully:', item.id);
  await logAction('create_content_item', 'content_item', item.id);
  return item;
}

export async function getContentItem(id: string): Promise<ContentItemWithVersion | null> {
  const { data, error } = await supabase
    .from('content_items')
    .select(`
      *,
      current_version:content_versions!fk_current_version(*),
      published_version:content_versions!fk_published_version(*),
      assigned_user:profiles!content_items_assigned_to_fkey(*)
    `)
    .eq('id', id)
    .single();

  if (error) {
    console.error('Error fetching content item:', error);
    return null;
  }

  return data;
}

export async function getContentItemsByStory(storyId: string): Promise<ContentItemWithVersion[]> {
  const { data, error } = await supabase
    .from('content_items')
    .select(`
      *,
      current_version:content_versions!fk_current_version(*),
      published_version:content_versions!fk_published_version(*),
      assigned_user:profiles!content_items_assigned_to_fkey(*)
    `)
    .eq('story_id', storyId)
    .order('is_canonical', { ascending: false });

  if (error) {
    console.error('Error fetching content items:', error);
    return [];
  }

  return data || [];
}

export async function assignContentItem(contentItemId: string, userId: string | null): Promise<boolean> {
  const { error } = await supabase
    .from('content_items')
    .update({ assigned_to: userId })
    .eq('id', contentItemId);

  if (error) {
    console.error('Error assigning content item:', error);
    return false;
  }

  await logAction('assign_content_item', 'content_item', contentItemId, { assigned_to: userId });
  return true;
}

export async function updateContentItem(id: string, updates: Partial<ContentItem>): Promise<ContentItem | null> {
  const { data, error } = await supabase
    .from('content_items')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating content item:', error);
    return null;
  }

  await logAction('update_content_item', 'content_item', id);
  return data;
}

export async function updateWorkflowStatus(id: string, status: WorkflowStatus, notes?: string): Promise<boolean> {
  const { error } = await supabase
    .from('content_items')
    .update({ workflow_status: status })
    .eq('id', id);

  if (error) {
    console.error('Error updating workflow status:', error);
    return false;
  }

  // Note: workflow_history is populated by trigger
  await logAction('workflow_transition', 'content_item', id, { to_status: status, notes });
  return true;
}

export async function publishContent(id: string): Promise<boolean> {
  const item = await getContentItem(id);
  if (!item || !item.current_version_id) return false;

  const { error } = await supabase
    .from('content_items')
    .update({
      published_version_id: item.current_version_id,
      published_at: new Date().toISOString(),
      workflow_status: 'published',
    })
    .eq('id', id);

  if (error) {
    console.error('Error publishing content:', error);
    return false;
  }

  await logAction('publish_content', 'content_item', id);
  return true;
}


// =============================================================================
// CONTENT VERSIONS
// =============================================================================

export async function createContentVersion(data: {
  content_item_id: string;
  body_ninjs: NinjsPayload;
  headline?: string;
  summary?: string;
  body_text?: string;
  duration_seconds?: number;
  version_type?: 'initial' | 'take' | 'update' | 'correction' | 'writethru';
  editorial_note?: string;
  is_auto_save?: boolean;
}): Promise<ContentVersion | null> {
  const user = await getCurrentUser();
  if (!user) return null;

  // Get next version number using RPC function
  const { data: nextNum, error: numError } = await supabase
    .rpc('get_next_version_number', { p_content_item_id: data.content_item_id });

  if (numError) {
    console.error('Error getting next version number:', numError);
    return null;
  }

  const { data: version, error } = await supabase
    .from('content_versions')
    .insert({
      content_item_id: data.content_item_id,
      version_number: nextNum,
      version_type: data.version_type || 'update',
      headline: data.headline,
      summary: data.summary,
      body_text: data.body_text,
      body_ninjs: data.body_ninjs,
      duration_seconds: data.duration_seconds,
      editorial_note: data.editorial_note,
      is_auto_save: data.is_auto_save || false,
      created_by: user.id,
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating content version:', error);
    return null;
  }

  // Update content item's current_version_id
  const { error: updateError } = await supabase
    .from('content_items')
    .update({ current_version_id: version.id })
    .eq('id', data.content_item_id);

  if (updateError) {
    console.error('Error updating current_version_id:', updateError);
  } else {
    console.log('Updated content item current_version_id to:', version.id);
  }

  if (!data.is_auto_save) {
    await logAction('create_version', 'content_version', version.id);
  }

  return version;
}

export async function getContentVersions(contentItemId: string): Promise<ContentVersion[]> {
  const { data, error } = await supabase
    .from('content_versions')
    .select('*')
    .eq('content_item_id', contentItemId)
    .order('version_number', { ascending: false });

  if (error) {
    console.error('Error fetching content versions:', error);
    return [];
  }

  return data || [];
}

export async function getContentVersion(id: string): Promise<ContentVersion | null> {
  const { data, error } = await supabase
    .from('content_versions')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    console.error('Error fetching content version:', error);
    return null;
  }

  return data;
}


// =============================================================================
// TRANSLATIONS
// =============================================================================

export async function createTranslation(data: {
  source_content_id: string;
  target_language: LanguageCode;
  translation_method?: TranslationMethod;
  mt_engine?: string;
}): Promise<{ translation: Translation; target_content: ContentItem } | null> {
  const user = await getCurrentUser();
  if (!user) return null;

  // Get source content
  const source = await getContentItem(data.source_content_id);
  if (!source) return null;

  // Create target content item
  const story = await getStory(source.story_id);
  if (!story) return null;

  const targetContent = await createContentItem({
    story_id: source.story_id,
    language: data.target_language,
    item_type: source.item_type,
    platform: source.platform,
    is_canonical: false,
    derived_from_content_id: source.id,
  });

  if (!targetContent) return null;

  // Create translation record using RPC
  const { data: translationId, error } = await supabase
    .rpc('create_translation_record', {
      p_source_content_id: source.id,
      p_target_content_id: targetContent.id,
      p_method: data.translation_method || 'human',
      p_translator_id: user.id,
    });

  if (error) {
    console.error('Error creating translation record:', error);
    return null;
  }

  const { data: translation } = await supabase
    .from('translations')
    .select('*')
    .eq('id', translationId)
    .single();

  await logAction('create_translation', 'translation', translationId);

  return {
    translation: translation!,
    target_content: targetContent,
  };
}

export async function getTranslations(sourceContentId: string): Promise<Translation[]> {
  const { data, error } = await supabase
    .from('translations')
    .select('*')
    .eq('source_content_id', sourceContentId);

  if (error) {
    console.error('Error fetching translations:', error);
    return [];
  }

  return data || [];
}

export async function getStaleTranslations(): Promise<Translation[]> {
  const { data, error } = await supabase
    .from('translations')
    .select('*')
    .eq('is_stale', true)
    .order('source_last_modified', { ascending: false });

  if (error) {
    console.error('Error fetching stale translations:', error);
    return [];
  }

  return data || [];
}

export async function updateTranslation(id: string, updates: Partial<Translation>): Promise<Translation | null> {
  const { data, error } = await supabase
    .from('translations')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating translation:', error);
    return null;
  }

  await logAction('update_translation', 'translation', id);
  return data;
}

export async function clearTranslationStale(id: string): Promise<boolean> {
  const { error } = await supabase.rpc('clear_translation_stale', { p_translation_id: id });

  if (error) {
    console.error('Error clearing translation stale:', error);
    return false;
  }

  return true;
}


// =============================================================================
// COLLECTIONS
// =============================================================================

export async function createCollection(data: {
  slug: string;
  type: CollectionType;
  ordering_mode: OrderingMode;
  platform: Platform;
  channel?: string;
  target_datetime?: string;
  scheduled_duration?: string;
}): Promise<Collection | null> {
  const user = await getCurrentUser();
  if (!user) return null;

  const { data: collection, error } = await supabase
    .from('collections')
    .insert({
      ...data,
      status: 'planning',
      ncs_id: 'TOLONEWS',
      created_by: user.id,
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating collection:', error);
    return null;
  }

  await logAction('create_collection', 'collection', collection.id);
  return collection;
}

export async function getCollection(id: string): Promise<Collection | null> {
  const { data, error } = await supabase
    .from('collections')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    console.error('Error fetching collection:', error);
    return null;
  }

  return data;
}

export async function getCollectionWithItems(id: string): Promise<CollectionWithItems | null> {
  const { data: collection, error: collError } = await supabase
    .from('collections')
    .select('*')
    .eq('id', id)
    .single();

  if (collError || !collection) {
    console.error('Error fetching collection:', collError);
    return null;
  }

  const { data: items, error: itemsError } = await supabase
    .from('collection_items')
    .select(`
      *,
      content_item:content_items(
        *,
        current_version:content_versions!fk_current_version(*)
      ),
      story:stories(*)
    `)
    .eq('collection_id', id)
    .order('sequence_order', { ascending: true });

  if (itemsError) {
    console.error('Error fetching collection items:', itemsError);
  }

  return {
    ...collection,
    items: items || [],
  };
}

export async function getCollections(options?: {
  type?: CollectionType;
  platform?: Platform;
  status?: string;
  from_date?: string;
  to_date?: string;
}): Promise<Collection[]> {
  let query = supabase
    .from('collections')
    .select('*')
    .order('target_datetime', { ascending: true });

  if (options?.type) {
    query = query.eq('type', options.type);
  }
  if (options?.platform) {
    query = query.eq('platform', options.platform);
  }
  if (options?.status) {
    query = query.eq('status', options.status);
  }
  if (options?.from_date) {
    query = query.gte('target_datetime', options.from_date);
  }
  if (options?.to_date) {
    query = query.lte('target_datetime', options.to_date);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching collections:', error);
    return [];
  }

  return data || [];
}

export async function updateCollection(id: string, updates: Partial<Collection>): Promise<Collection | null> {
  const { data, error } = await supabase
    .from('collections')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating collection:', error);
    return null;
  }

  await logAction('update_collection', 'collection', id);
  return data;
}

export async function lockCollection(id: string): Promise<boolean> {
  const user = await getCurrentUser();
  if (!user) return false;

  const { data, error } = await supabase
    .rpc('lock_collection', { p_collection_id: id, p_user_id: user.id });

  if (error) {
    console.error('Error locking collection:', error);
    return false;
  }

  return data;
}

export async function unlockCollection(id: string): Promise<boolean> {
  const user = await getCurrentUser();
  if (!user) return false;

  const { data, error } = await supabase
    .rpc('unlock_collection', { p_collection_id: id, p_user_id: user.id });

  if (error) {
    console.error('Error unlocking collection:', error);
    return false;
  }

  return data;
}


// =============================================================================
// COLLECTION ITEMS
// =============================================================================

export async function addToCollection(data: {
  collection_id: string;
  content_item_id: string;
  sequence_order?: number;
  planned_duration?: string;
  segment_code?: string;
  item_type_broadcast?: BroadcastItemType;
  priority?: number;
  is_featured?: boolean;
}): Promise<CollectionItem | null> {
  const user = await getCurrentUser();
  if (!user) return null;

  const { data: item, error } = await supabase
    .from('collection_items')
    .insert({
      ...data,
      is_pinned: false,
      is_featured: data.is_featured || false,
      status: 'scheduled',
      added_by: user.id,
    })
    .select()
    .single();

  if (error) {
    console.error('Error adding to collection:', error);
    return null;
  }

  await logAction('add_to_collection', 'collection_item', item.id);
  return item;
}

export async function updateCollectionItem(id: string, updates: Partial<CollectionItem>): Promise<CollectionItem | null> {
  const { data, error } = await supabase
    .from('collection_items')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating collection item:', error);
    return null;
  }

  await logAction('update_collection_item', 'collection_item', id);
  return data;
}

export async function removeFromCollection(id: string): Promise<boolean> {
  const { error } = await supabase
    .from('collection_items')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error removing from collection:', error);
    return false;
  }

  await logAction('remove_from_collection', 'collection_item', id);
  return true;
}

export async function reorderCollectionItems(collectionId: string, orderedIds: string[]): Promise<boolean> {
  const updates = orderedIds.map((id, index) => ({
    id,
    sequence_order: index + 1,
  }));

  for (const update of updates) {
    const { error } = await supabase
      .from('collection_items')
      .update({ sequence_order: update.sequence_order })
      .eq('id', update.id);

    if (error) {
      console.error('Error reordering collection items:', error);
      return false;
    }
  }

  await logAction('reorder_collection', 'collection', collectionId);
  return true;
}


// =============================================================================
// COMPONENTS
// =============================================================================

export async function createComponent(data: {
  story_id: string;
  component_type: 'lower_third' | 'quote_card' | 'locator' | 'headline_graphic';
  canonical_language: LanguageCode;
  line1: string;
  line2?: string;
  timecode_in?: string;
  timecode_out?: string;
}): Promise<Component | null> {
  const user = await getCurrentUser();
  if (!user) return null;

  const translations = {
    [data.canonical_language]: {
      line1: data.line1,
      line2: data.line2,
    },
  };

  const { data: component, error } = await supabase
    .from('components')
    .insert({
      story_id: data.story_id,
      component_type: data.component_type,
      canonical_language: data.canonical_language,
      translations,
      timecode_in: data.timecode_in,
      timecode_out: data.timecode_out,
      is_active: true,
      created_by: user.id,
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating component:', error);
    return null;
  }

  await logAction('create_component', 'component', component.id);
  return component;
}

export async function getComponents(storyId: string): Promise<Component[]> {
  const { data, error } = await supabase
    .from('components')
    .select('*')
    .eq('story_id', storyId)
    .eq('is_active', true)
    .order('created_at', { ascending: true });

  if (error) {
    console.error('Error fetching components:', error);
    return [];
  }

  return data || [];
}

export async function updateComponent(id: string, updates: Partial<Component>): Promise<Component | null> {
  const { data, error } = await supabase
    .from('components')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating component:', error);
    return null;
  }

  await logAction('update_component', 'component', id);
  return data;
}

export async function addComponentTranslation(
  id: string,
  language: LanguageCode,
  line1: string,
  line2?: string
): Promise<boolean> {
  const { data, error } = await supabase.rpc('add_component_translation', {
    p_component_id: id,
    p_language: language,
    p_line1: line1,
    p_line2: line2 || '',
  });

  if (error) {
    console.error('Error adding component translation:', error);
    return false;
  }

  return data;
}


// =============================================================================
// APPROVALS
// =============================================================================

export async function createApproval(data: {
  content_item_id: string;
  content_version_id?: string;
  approval_stage: ApprovalStage;
}): Promise<Approval | null> {
  const { data: approval, error } = await supabase
    .from('approvals')
    .insert({
      ...data,
      decision: 'pending',
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating approval:', error);
    return null;
  }

  return approval;
}

export async function updateApproval(
  id: string,
  decision: ApprovalDecision,
  comments?: string,
  revision_requested_fields?: string[]
): Promise<Approval | null> {
  const user = await getCurrentUser();
  if (!user) return null;

  const { data, error } = await supabase
    .from('approvals')
    .update({
      decision,
      comments,
      revision_requested_fields,
      approver_id: user.id,
      decided_at: new Date().toISOString(),
    })
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating approval:', error);
    return null;
  }

  await logAction('approval_decision', 'approval', id, { decision });
  return data;
}

export async function getPendingApprovals(userId?: string): Promise<Approval[]> {
  let query = supabase
    .from('approvals')
    .select('*, content_items(*, stories(*))')
    .eq('decision', 'pending')
    .order('submitted_at', { ascending: true });

  if (userId) {
    query = query.eq('approver_id', userId);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching pending approvals:', error);
    return [];
  }

  return data || [];
}


// =============================================================================
// EDITORIAL COMMENTS
// =============================================================================

export async function createEditorialComment(data: {
  content_item_id: string;
  content_version_id?: string;
  comment_text: string;
  field_reference?: string;
  position_start?: number;
  position_end?: number;
}): Promise<EditorialComment | null> {
  const user = await getCurrentUser();
  if (!user) return null;

  const { data: comment, error } = await supabase
    .from('editorial_comments')
    .insert({
      ...data,
      author_id: user.id,
      is_resolved: false,
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating editorial comment:', error);
    return null;
  }

  return comment;
}

export async function getEditorialComments(contentItemId: string): Promise<EditorialComment[]> {
  const { data, error } = await supabase
    .from('editorial_comments')
    .select('*, author:profiles!editorial_comments_author_id_fkey(*)')
    .eq('content_item_id', contentItemId)
    .order('created_at', { ascending: true });

  if (error) {
    console.error('Error fetching editorial comments:', error);
    return [];
  }

  return data || [];
}

export async function resolveEditorialComment(id: string): Promise<boolean> {
  const user = await getCurrentUser();
  if (!user) return false;

  const { error } = await supabase
    .from('editorial_comments')
    .update({
      is_resolved: true,
      resolved_by: user.id,
      resolved_at: new Date().toISOString(),
    })
    .eq('id', id);

  if (error) {
    console.error('Error resolving editorial comment:', error);
    return false;
  }

  return true;
}


// =============================================================================
// COORDINATED RELEASES
// =============================================================================

export async function createCoordinatedRelease(data: {
  story_id: string;
  go_live_time: string;
  notes?: string;
}): Promise<CoordinatedRelease | null> {
  const user = await getCurrentUser();
  if (!user) return null;

  const { data: release, error } = await supabase
    .from('coordinated_releases')
    .insert({
      ...data,
      status: 'scheduled',
      created_by: user.id,
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating coordinated release:', error);
    return null;
  }

  await logAction('create_release', 'coordinated_release', release.id);
  return release;
}

export async function addReleaseTarget(data: {
  release_id: string;
  platform: Platform;
  content_item_id?: string;
  collection_id?: string;
}): Promise<ReleaseTarget | null> {
  const { data: target, error } = await supabase
    .from('release_targets')
    .insert({
      ...data,
      status: 'scheduled',
    })
    .select()
    .single();

  if (error) {
    console.error('Error adding release target:', error);
    return null;
  }

  return target;
}

export async function getUpcomingReleases(hoursAhead: number = 24): Promise<CoordinatedRelease[]> {
  const { data, error } = await supabase.rpc('get_upcoming_releases', { p_hours_ahead: hoursAhead });

  if (error) {
    console.error('Error fetching upcoming releases:', error);
    return [];
  }

  return data || [];
}

export async function executeRelease(id: string): Promise<boolean> {
  const { data, error } = await supabase.rpc('execute_release', { p_release_id: id });

  if (error) {
    console.error('Error executing release:', error);
    return false;
  }

  await logAction('execute_release', 'coordinated_release', id);
  return data;
}


// =============================================================================
// TERMINOLOGY
// =============================================================================

export async function getTerminology(): Promise<Terminology[]> {
  const { data, error } = await supabase
    .from('terminology')
    .select('*')
    .order('term_en', { ascending: true });

  if (error) {
    console.error('Error fetching terminology:', error);
    return [];
  }

  return data || [];
}

export async function createTerminology(data: Partial<Terminology>): Promise<Terminology | null> {
  const { data: term, error } = await supabase
    .from('terminology')
    .insert(data)
    .select()
    .single();

  if (error) {
    console.error('Error creating terminology:', error);
    return null;
  }

  return term;
}


// =============================================================================
// USER & AUTH HELPERS
// =============================================================================

export async function getCurrentUser(): Promise<User | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { data: profile, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', user.id)
    .single();

  if (error) {
    console.error('Error fetching user profile:', error);
    return null;
  }

  return profile;
}

export async function getUserProfile(userId: string): Promise<User | null> {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single();

  if (error) {
    console.error('Error fetching user profile:', error);
    return null;
  }

  return data;
}


// =============================================================================
// AUDIT LOGGING
// =============================================================================

export async function logAction(
  action: string,
  entity_type?: string,
  entity_id?: string,
  extra_data?: Record<string, unknown>
): Promise<void> {
  const user = await getCurrentUser();

  await supabase.from('audit_logs').insert({
    user_id: user?.id,
    action,
    entity_type,
    entity_id,
    new_values: extra_data,
  });
}


// =============================================================================
// NOTIFICATIONS
// =============================================================================

export async function getPendingNotifications(): Promise<UpdateNotification[]> {
  const user = await getCurrentUser();
  if (!user) return [];

  const { data, error } = await supabase.rpc('get_pending_notifications', { p_user_id: user.id });

  if (error) {
    console.error('Error fetching notifications:', error);
    return [];
  }

  return data || [];
}

export async function acknowledgeNotification(
  id: string,
  action: 'updated' | 'no_action_needed' | 'deferred'
): Promise<boolean> {
  const user = await getCurrentUser();
  if (!user) return false;

  const { data, error } = await supabase.rpc('acknowledge_notification', {
    p_notification_id: id,
    p_user_id: user.id,
    p_action: action,
  });

  if (error) {
    console.error('Error acknowledging notification:', error);
    return false;
  }

  return data;
}


// =============================================================================
// REAL-TIME SUBSCRIPTIONS
// =============================================================================

export function subscribeToStoryChanges(
  storyId: string,
  callback: (payload: unknown) => void
) {
  return supabase
    .channel(`story-${storyId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'content_items',
        filter: `story_id=eq.${storyId}`,
      },
      callback
    )
    .subscribe();
}

export function subscribeToCollectionChanges(
  collectionId: string,
  callback: (payload: unknown) => void
) {
  return supabase
    .channel(`collection-${collectionId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'collection_items',
        filter: `collection_id=eq.${collectionId}`,
      },
      callback
    )
    .subscribe();
}

export function subscribeToMosUpdates(callback: (payload: unknown) => void) {
  return supabase
    .channel('mos-updates')
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'collection_items',
        filter: 'mos_item_status=neq.null',
      },
      callback
    )
    .subscribe();
}


// =============================================================================
// WORKFLOW HISTORY
// =============================================================================

export async function getWorkflowHistory(contentItemId: string): Promise<WorkflowHistory[]> {
  const { data, error } = await supabase
    .from('workflow_history')
    .select('*, triggered_by_user:profiles!workflow_history_triggered_by_fkey(*)')
    .eq('content_item_id', contentItemId)
    .order('triggered_at', { ascending: false });

  if (error) {
    console.error('Error fetching workflow history:', error);
    return [];
  }

  return data || [];
}


// =============================================================================
// SEARCH
// =============================================================================

export async function searchContent(query: string): Promise<ContentItemWithVersion[]> {
  const { data, error } = await supabase
    .from('content_versions')
    .select(`
      content_item:content_items(
        *,
        story:stories(*)
      )
    `)
    .or(`headline.ilike.%${query}%,body_text.ilike.%${query}%`)
    .limit(50);

  if (error) {
    console.error('Error searching content:', error);
    return [];
  }

  // Flatten and dedupe
  const items = (data?.map((v) => v.content_item).filter(Boolean) || []) as unknown as ContentItemWithVersion[];
  const uniqueIds = new Set<string>();
  return items.filter((item) => {
    if (uniqueIds.has(item.id)) return false;
    uniqueIds.add(item.id);
    return true;
  });
}


// =============================================================================
// ADMIN: USER MANAGEMENT
// =============================================================================

export async function getUsers(): Promise<User[]> {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .order('full_name', { ascending: true });

  if (error) {
    console.error('Error fetching users:', error);
    return [];
  }

  return data || [];
}

export async function updateUserRole(userId: string, role: string): Promise<boolean> {
  const { error } = await supabase
    .from('profiles')
    .update({ role })
    .eq('id', userId);

  if (error) {
    console.error('Error updating user role:', error);
    return false;
  }

  await logAction('update_user_role', 'user', userId, { new_role: role });
  return true;
}

export async function deleteUser(userId: string): Promise<boolean> {
  // Soft delete by setting role to suspended
  const { error } = await supabase
    .from('profiles')
    .update({ role: 'suspended' })
    .eq('id', userId);

  if (error) {
    console.error('Error suspending user:', error);
    return false;
  }

  await logAction('suspend_user', 'user', userId);
  return true;
}


// =============================================================================
// ADMIN: AUDIT LOGS
// =============================================================================

export interface AuditLogEntry {
  id: string;
  user_id: string | null;
  action: string;
  entity_type: string | null;
  entity_id: string | null;
  old_values: Record<string, unknown> | null;
  new_values: Record<string, unknown> | null;
  created_at: string;
  user?: User;
}

export async function getLogs(limit: number = 100): Promise<AuditLogEntry[]> {
  const { data, error } = await supabase
    .from('audit_logs')
    .select('*, user:profiles!audit_logs_user_id_fkey(*)')
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    console.error('Error fetching audit logs:', error);
    return [];
  }

  return data || [];
}


// =============================================================================
// ADMIN: TERMINOLOGY (Legacy format support)
// =============================================================================

export interface TerminologyItem {
  id: string;
  original: string;
  replacement: string;
  language: string;
  type: 'forbidden' | 'standard';
  context?: string;
  createdBy?: string;
  createdAt?: string;
}

export async function saveTerminology(term: TerminologyItem, userId: string): Promise<boolean> {
  const { error } = await supabase
    .from('terminology')
    .insert({
      id: term.id,
      term_en: term.original,
      term_dari: term.language === 'fa-AF' ? term.replacement : null,
      term_pashto: term.language === 'ps-AF' ? term.replacement : null,
      notes: term.context,
      created_by: userId,
    });

  if (error) {
    console.error('Error saving terminology:', error);
    return false;
  }

  await logAction('create_terminology', 'terminology', term.id);
  return true;
}

export async function deleteTerminology(id: string, userId: string): Promise<boolean> {
  const { error } = await supabase
    .from('terminology')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting terminology:', error);
    return false;
  }

  await logAction('delete_terminology', 'terminology', id);
  return true;
}


// =============================================================================
// SYSTEM PROMPTS MANAGEMENT
// =============================================================================

export interface SystemPrompt {
  id: string;
  agent_name: string;
  version: number;
  prompt_text: string;
  is_active: boolean;
  created_by: string | null;
  created_at: string;
  activated_at: string | null;
  activated_by: string | null;
  notes: string | null;
  // Joined data
  created_by_user?: User;
  activated_by_user?: User;
}

export interface PromptUsageLog {
  id: string;
  prompt_id: string;
  agent_name: string;
  prompt_version: number;
  used_at: string;
  content_item_id: string | null;
  success: boolean;
  error_message: string | null;
}

// Get all prompts for an agent (all versions)
export async function getPromptsForAgent(agentName: string): Promise<SystemPrompt[]> {
  const { data, error } = await supabase
    .from('system_prompts')
    .select(`
      *,
      created_by_user:profiles!system_prompts_created_by_fkey(*),
      activated_by_user:profiles!system_prompts_activated_by_fkey(*)
    `)
    .eq('agent_name', agentName)
    .order('version', { ascending: false });

  if (error) {
    console.error('Error fetching prompts:', error);
    return [];
  }

  return data || [];
}

// Get active prompt for an agent
export async function getActivePrompt(agentName: string): Promise<SystemPrompt | null> {
  const { data, error } = await supabase
    .from('system_prompts')
    .select('*')
    .eq('agent_name', agentName)
    .eq('is_active', true)
    .single();

  if (error) {
    console.error('Error fetching active prompt:', error);
    return null;
  }

  return data;
}

// Get all agents with their active prompts
export async function getAllActivePrompts(): Promise<SystemPrompt[]> {
  const { data, error } = await supabase
    .from('system_prompts')
    .select(`
      *,
      created_by_user:profiles!system_prompts_created_by_fkey(*),
      activated_by_user:profiles!system_prompts_activated_by_fkey(*)
    `)
    .eq('is_active', true)
    .order('agent_name', { ascending: true });

  if (error) {
    console.error('Error fetching active prompts:', error);
    return [];
  }

  return data || [];
}

// Create a new prompt version
export async function createPromptVersion(
  agentName: string,
  promptText: string,
  notes?: string,
  activate: boolean = false
): Promise<string | null> {
  const { data, error } = await supabase.rpc('create_prompt_version', {
    p_agent_name: agentName,
    p_prompt_text: promptText,
    p_notes: notes || null,
    p_activate: activate,
  });

  if (error) {
    console.error('Error creating prompt version:', error);
    return null;
  }

  await logAction('create_prompt_version', 'system_prompt', data, { agent_name: agentName, activate });
  return data;
}

// Activate a specific prompt version
export async function activatePrompt(promptId: string): Promise<boolean> {
  const { data, error } = await supabase.rpc('activate_prompt', {
    p_prompt_id: promptId,
  });

  if (error) {
    console.error('Error activating prompt:', error);
    return false;
  }

  await logAction('activate_prompt', 'system_prompt', promptId);
  return data;
}

// Get prompt usage statistics
export async function getPromptUsageStats(agentName: string, days: number = 7): Promise<{
  total: number;
  successful: number;
  failed: number;
  byVersion: Record<number, { total: number; successful: number }>;
}> {
  const since = new Date();
  since.setDate(since.getDate() - days);

  const { data, error } = await supabase
    .from('prompt_usage_log')
    .select('*')
    .eq('agent_name', agentName)
    .gte('used_at', since.toISOString());

  if (error) {
    console.error('Error fetching usage stats:', error);
    return { total: 0, successful: 0, failed: 0, byVersion: {} };
  }

  const stats = {
    total: data?.length || 0,
    successful: data?.filter(l => l.success).length || 0,
    failed: data?.filter(l => !l.success).length || 0,
    byVersion: {} as Record<number, { total: number; successful: number }>,
  };

  data?.forEach(log => {
    if (!stats.byVersion[log.prompt_version]) {
      stats.byVersion[log.prompt_version] = { total: 0, successful: 0 };
    }
    stats.byVersion[log.prompt_version].total++;
    if (log.success) {
      stats.byVersion[log.prompt_version].successful++;
    }
  });

  return stats;
}
